<template>
  <v-dialog v-model="dialog" persistent max-width="600">
    <v-card class="rounded-xl">
      <!-- Header with gradient background -->
      <v-card-title class="white--text gradient-header py-4">
        <div class="d-flex align-center">
          <v-icon large color="white" class="mr-3">mdi-cancel</v-icon>
          <span class="text-h5 font-weight-medium">Reject Response</span>
        </div>
        <v-spacer />
        <v-btn icon dark @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-container fluid class="pa-0">
          <!-- Need Title -->
          <v-row v-if="need" class="mb-4" >
            <v-col cols="12">
              <v-card flat class="rounded-lg pa-4" color="#f8f9fa">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-format-title</v-icon>
                  <h3 class="text-h6 primary--text">Need</h3>
                </div>
                <v-divider class="my-3"></v-divider>
                <p class="text-subtitle-1 font-weight-medium">{{ need.title }}</p>
              </v-card>
            </v-col>
          </v-row>

          <!-- Responder Information -->
          <v-row class="mb-4" v-if="response">
            <v-col cols="12">
              <v-card flat class="rounded-lg pa-4" color="#f8f9fa">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>
                  <h3 class="text-h6 primary--text">Responder</h3>
                </div>
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center">
                  <editable-avatar
                    :image="response.volunteer?.thumbnail || response.volunteer?.image || response.company?.thumbnail || response.company?.image || require('@/assets/default.jpg')"
                    :editable="false"
                    :size="60"
                    class="mr-4"
                  />
                  <div>
                    <p class="text-subtitle-1 font-weight-medium mb-1">
                      {{ response.volunteer ? 
                        `${response.volunteer.first_name} ${response.volunteer.last_name}` : 
                        response.company?.name || 'Unknown Responder' }}
                    </p>
                    <v-chip small color="blue lighten-5" class="blue--text">
                      {{ response.volunteer ? 'Volunteer' : 'Company' }}
                    </v-chip>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Shift Information -->
          <v-row class="mb-4">
            <v-col cols="12">
              <v-card flat class="rounded-lg pa-4" color="#f8f9fa">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                  <h3 class="text-h6 primary--text">Shift</h3>
                </div>
                <v-divider class="my-3"></v-divider>
                <p class="text-subtitle-1 font-weight-medium">{{ shiftTitle || 'No shift specified' }}</p>
              </v-card>
            </v-col>
          </v-row>

          <!-- Rejection Reason -->
          <v-row>
            <v-col cols="12">
              <v-card flat class="rounded-lg pa-4" color="#f8f9fa">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-comment-text-outline</v-icon>
                  <h3 class="text-h6 primary--text">Reason for Rejection</h3>
                  <tip-icon class="ml-2">
                    Type any additional notes about why you are rejecting this response.
                  </tip-icon>
                </div>
                <v-divider class="mb-3"></v-divider>
                <v-textarea
                  v-model="reply"
                  label="Message"
                  rows="3"
                  outlined
                  auto-grow
                  class="rounded-lg"
                  background-color="white"
                  :error-messages="replyMessage"
                  @input="onChange"
                ></v-textarea>
              </v-card>
            </v-col>
          </v-row>

          <!-- Error Message -->
          <v-row v-if="error">
            <v-col cols="12">
              <v-alert type="error" class="rounded-lg">
                {{ error }}
              </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer />
        <v-btn
          color="#fff darken-1"
          large
          depressed
          class="px-6 rounded-lg mr-3"
          @click="onClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="error"
          large
          depressed
          class="px-6 rounded-lg mr-3"
          :loading="loading"
          @click="onSubmitNeedResponse"
        >
          {{ already ? "Update Rejection" : "Reject" }}
        </v-btn>
        <v-btn
          color="blue darken-1"
          large
          depressed
          class="px-6 rounded-lg"
          :loading="cancelling"
          @click="onWithdrawResponse"
          v-if="already"
        >
          Withdraw
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TipIcon from "@/components/common/TipIcon.vue";
import { mapActions, mapState } from "vuex";
import EditableAvatar from "../../../components/EditableAvatar.vue";

export default {
  components: {
    TipIcon,
    EditableAvatar
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    need: Object,
    response: Object,
    shiftTitle: String,
    onCloseDialog: {
      type: Function
    },
    onRejected: {
      type: Function
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      reply: null,
      replyMessage: null,
      already: false,
      cancelling: false
    };
  },
  methods: {
    ...mapActions({
      rejectResponse: "response/rejectResponse"
    }),
    onClose() {
      this.reply = null;
      this.error = null;
      this.onCloseDialog();
    },
    onChange() {
      this.error = null;
    },
    onSubmitNeedResponse() {
      this.error = null;
      this.loading = true;
      this.rejectResponse({ _id: this.response._id, reply: this.reply })
        .then(res => {
          this.loading = false;
          this.reply = "";
          this.onRejected(res);
        })
        .catch(error => {
          this.loading = false;
          this.error = error.response?.data?.message || "An error occurred while rejecting the response";
        });
    },
    onWithdrawResponse() {
      // Implement withdraw functionality
      this.cancelling = true;
      // Your withdraw logic here
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    })
  }
};
</script>

<style scoped>
.gradient-header {
  background: linear-gradient(135deg,#084468,#0a5b8a);
}

.v-card {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.rounded-xl {
  border-radius: 16px !important;
}

.rounded-lg {
  border-radius: 12px !important;
}

.v-textarea::v-deep textarea {
  color: #333 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: normal;
}
</style>